import { Promo } from 'Cloud/Application/Promo';
import { promoController } from 'Cloud/Application/PromoController';
import { logger } from 'lib/logger';
import { xray } from 'lib/xray';
import { HIDE_ADS, IS_FRAME_PAGES, IS_PUBLIC_FOLDER, IS_REACT_PROMO_NEW_YEAR } from 'reactApp/appHelpers/configHelpers';
import {
    disableReduxPromoAfter3Promo,
    FILL_SAGA_PROMO_QUEUE_TIMEOUT,
    NEWPROMO_MANAGER_CHECK_INTERVAL,
    NO_LOCK_LAST_SAGA_PROMO,
    prune3promoFakeItemFromSagaQueue,
} from 'reactApp/appHelpers/featuresHelpers/features/3promoMigration';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { initInitMobAppPromo } from 'reactApp/modules/promo/promoMobileApp/promoMobileApp.saga';
import { initAiSetFakedoorPromo, showAiSetFakedoorPromo } from 'reactApp/modules/promo/sagas/aiSetFakedoor.saga';
import { initAutologinPromos, showAutologinPromos } from 'reactApp/modules/promo/sagas/autologin.saga';
import { initAutosaveAsidePromoModal, showAutosaveAsidePromoModal } from 'reactApp/modules/promo/sagas/autosave.saga';
import { initAutoUploadAsidePromo, showAutoUploadAsidePromo } from 'reactApp/modules/promo/sagas/autoUploadPromo.saga';
import { initAutumnPromoModal, showAutumnPromoModal } from 'reactApp/modules/promo/sagas/autumnPromoModal';
import { initBannerPromo, showBannerPromo } from 'reactApp/modules/promo/sagas/banner.saga';
import { initBindingSpherumPromoModal, showBindingSpherumPromoModal } from 'reactApp/modules/promo/sagas/bindingSpherum.saga';
import { initBlackFriday2024Modal, showBlackFriday2024Modal } from 'reactApp/modules/promo/sagas/blackFriday2024Modal';
import { initCrossPromoVkMusicModal, showCrossPromoVkMusicModal } from 'reactApp/modules/promo/sagas/crossPromoVkMusic.saga';
import { initCyberMonday2025Modal, showCyberMonday2025Modal } from 'reactApp/modules/promo/sagas/cyberMonday2025Modal';
import { initDownloadAppPromo, showDownloadAppPromo } from 'reactApp/modules/promo/sagas/downloadApp.saga';
import { initEpubPromo } from 'reactApp/modules/promo/sagas/epub.saga';
import {
    initExpiredDeviceSubscriptionPromo,
    showExpiredDeviceSubscriptionPromo,
} from 'reactApp/modules/promo/sagas/expiredDeviceSubscription.saga';
import { initFaceFilterPromo } from 'reactApp/modules/promo/sagas/faceFilter.saga';
import { initFlexibleQuotaPromo, showFlexibleQuotaPromo } from 'reactApp/modules/promo/sagas/flexibleQuotaPromo';
import { initGamerBundleModal, showGamerBundleModal } from 'reactApp/modules/promo/sagas/gamerBundle.saga';
import { initMobilePublicBannerPromo, showMobilePublicBannerPromo } from 'reactApp/modules/promo/sagas/mobilePublicBanner.saga';
import { initMobileSearchBanner, showMobileSearchBanner } from 'reactApp/modules/promo/sagas/mobileSearchBanner.saga';
import { initMobileSearchHistoryBanner, showMobileSearchHistoryBanner } from 'reactApp/modules/promo/sagas/mobileSearchHistoryBanner.saga';
import { initPreOverquotaPromo, showPreOverquotaPromo } from 'reactApp/modules/promo/sagas/preOverquotaPromo.saga';
import { initPublicUploadPromo } from 'reactApp/modules/promo/sagas/publicUpload.saga';
import { initQuotaPromo, showQuotaPopup } from 'reactApp/modules/promo/sagas/quota.saga';
import { initRebrandingPromo, showRebrandingPromoModal } from 'reactApp/modules/promo/sagas/rebranding.saga';
import { initReferralProgramPromoModal, showReferralProgramPromoModal } from 'reactApp/modules/promo/sagas/referralProgramPromo.saga';
import { initRestoreTrashbinFilesModal, showRestoreTrashbinFilesModal } from 'reactApp/modules/promo/sagas/restoreTrashbinFilesModal';
import { initSecondClickPromo } from 'reactApp/modules/promo/sagas/secondClick.saga';
import { initWelcomeNewFamilyTariffDialog, showFamilyBuyModal } from 'reactApp/modules/promo/sagas/showFamilyBuyModal';
import { initThemeAsidePromoModal, showThemeAsidePromoModal } from 'reactApp/modules/promo/sagas/themeAsidePromoModal';
import { initThemedPublicPromo, showThemedPublicPromo } from 'reactApp/modules/promo/sagas/themedPublicPromo.saga';
import { initUsedSpacePostpaidModal, showUsedSpacePostpaidModal } from 'reactApp/modules/promo/sagas/usedSpacePostpaidModal';
import { initVigboTariffEnd, showVigboTariffEnd } from 'reactApp/modules/promo/sagas/vigboEndTariff.saga';
import { initVigboRenewRemind, showVigboRenewRemind } from 'reactApp/modules/promo/sagas/vigboRenewRemind.saga';
import { initVigboSuccessBuyModal, showVigboSuccessBuyModal } from 'reactApp/modules/promo/sagas/vigboSuccessBuyModal';
import { initWhatsNewPromo, showWhatsNewPromo } from 'reactApp/modules/promo/sagas/whatsNew.saga';
import { setBottomMarginAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { setUserData } from 'reactApp/modules/user/user.actions';
import { getUserIsLoaded } from 'reactApp/modules/user/user.selectors';
import { noop } from 'reactApp/utils/helpers';
import { cancel, delay, put, race, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { type SagaGenerator, call, select } from 'typed-redux-saga';

import { getPromoManager, updatePromoManager } from '../promoNew/manager';
import { promoSagaLogger } from './promo.helpers';
import {
    addPromoToShowQueue,
    clearCurrentPromo,
    clearQueue,
    initPromoController,
    promoShown,
    removePromo,
    showMobileAppPromo,
    showPromo,
    skipPromo,
} from './promo.module';
import { isPromoCanBeShown, PromoSelectors } from './promo.selectors';
import { initAutoUploadOnboardingPromo, showAutoUploadOnboardingPromo } from './sagas/autoUploadOnboardingPromo.saga';
import { initBeautifulAddressPromo, showBeautifulAddressPromo } from './sagas/beautifulAddressPromo.saga';
import { initChangePublicDesignPromoToolbarInit } from './sagas/changePublicDesignPromoToolbar';
import { initCloudNewFeaturesMobilePromo } from './sagas/cloudNewFeaturesMobile.saga';
import { initCoverageCampaignAsidePromo, showCoverageCampaignAsidePromo } from './sagas/coverageCampaign.saga';
import { initDefaultTariffForkModal, showDefaultTariffForkModal } from './sagas/defaultTariffForkModal.saga';
import { initDemoPromoTooltip } from './sagas/demoPromoTooltip.saga';
import { initDisableAdsBanner } from './sagas/disableAdsBanner';
import { initDiscountInOutflowLastDayPromo, showDiscountInOutflowLastDayPromo } from './sagas/discountInOutflowLastDayPromo';
import { initDiskOAsidePromoModal, showDiskOAsidePromoModal } from './sagas/diskOAsidePromoModal';
import { initEditorLLMButton } from './sagas/editorLLMButton.saga';
import { initExtendQuotaOfferModal, showExtendQuotaOfferModal } from './sagas/extendQuotaOffer.saga';
import { initFamilyAsidePromoModal, showFamilyAsidePromoModal } from './sagas/familyAsidePromoModal';
import { initFullscreen, showFullscreenDialog } from './sagas/fullscreenMonetization.saga';
import { closeCSATSurvey, handlePushCSATTriggerEvent, initCSATSurvey, pushCSATTriggerEvent, showCSATSurvey } from './sagas/initCSATSurvey';
import { initLinkVigboAccount } from './sagas/linkVigboAccount.saga';
import { initLitresModal, showLitresModal } from './sagas/litresModal.saga';
import { initMailSpaceTrialPromoModal, showMailSpaceTrialPromoModal } from './sagas/mailSpaceTrialPromo';
import { initPaidUserReturnModal, showPaidUserReturnModal } from './sagas/paidUserReturn.saga';
import { initParnersDiscountAsidePromoModal, showParnersDiscountAsidePromoModal } from './sagas/parnersDiscount.saga';
import { initPdfWorkWithPagesPromo } from './sagas/pdfWorkWithPagesPromo.saga';
import { initReferralProgramAsidePromo, showReferralProgramAsideModal } from './sagas/referralProgramAsidePromo.saga';
import { initReferralProgramSpacePromo } from './sagas/referralProgramSpacePromo.saga';
import { initSafetyAsidePromoModal, showSafetyAsidePromoModal } from './sagas/safetyAsidePromoModal';
import { initSearchTooltipPromo } from './sagas/searchTooltipPromo.saga';
import { initCoEditPopupPromo } from './sagas/shareButtonCoEditPopup.saga';
import { showFamilyAlbumsTooltip, showFamilyAsideMenuTooltip, showFamilyDocumentsTooltip } from './sagas/showFamilyTooltips.saga';
import { initSidebarTrialPromoModal, showSidebarTrialPromoModal } from './sagas/sidebarTrialPromo';
import { initStoriesInGalleyPromo } from './sagas/storiesInGalleryPromo.saga';
import { initSuggestionsLawPromo, showSuggestionsLawPromo } from './sagas/suggestionsLaw.saga';
import { initTariffSelectionPoll, showTariffSelectionPoll } from './sagas/tariffSelection.saga';
import { initTopBanner } from './sagas/topBanner.saga';
import { initVkMusicBundleModal, showVkMusicBundleModal } from './sagas/vkMusicBundle.saga';

const PERIOD_BETWEEN_PROMO = 1 * 1000;
const PROMO_WAIT_PERIOD = 500;
const PROMO_CONTROLLER_CHECK_INTERVAL = 500;

function* awaitUserLoad() {
    const isLoaded = yield* select(getUserIsLoaded);
    if (!isLoaded) {
        yield take(setUserData);
    }
}

export function* addOneOf(...args: (() => SagaGenerator<any, any> | void)[]) {
    const queueAtBeginning = yield* select(PromoSelectors.getPromoQueue);
    for (const arg of args) {
        yield arg();
        const queue = yield* select(PromoSelectors.getPromoQueue);
        if (queue.length > queueAtBeginning.length) {
            return;
        }
    }
}

// stops old promoController
promoController.register(
    1,
    Object.assign(new Promo('for-wait'), {
        isEnabled() {
            return true;
        },
    })
);

function firebasePromo() {
    promoController.markAsShown('for-wait');
    (window as any).__FB?.requestPermission();
}

function* fillQueueWithTimeout(timeout: number) {
    const { isTimeout } = yield race({
        done: fillQueue(),
        isTimeout: delay(timeout),
    });

    if (isTimeout) {
        xray.send('promo-saga-fill-q-timeout', { dwh: { timeout } });
    }
}

function* fillQueue() {
    const isPhone = yield* select(EnvironmentSelectors.isPhone);
    // заполняем queue, приоритет промок в зависимости от последовательности
    // https://confluence.vk.team/pages/viewpage.action?pageId=915075918

    /**
     * ATTENTION!
     * ALARM!
     * Проверь можно ли твоему баннеру светиться в вэбвью или других узких кейсах как viewContent
     * Полезные флаги: IS_WEBVIEW, isViewContentOnly
     */

    yield initAutologinPromos(); // https://jira.vk.team/browse/CLOUDWEB-16865

    if (isPhone) {
        yield initCSATSurvey(); // https://jira.vk.team/browse/CLOUDWEB-18609

        // Mobile
        yield addOneOf(
            initPreOverquotaPromo, // https://jira.vk.team/browse/CLOUDWEB-19123 Показ модалки для бывших оверквотников
            // tempexp_18926-next-line
            initAutoUploadOnboardingPromo, // https://jira.vk.team/browse/CLOUDWEB-18926
            initExtendQuotaOfferModal, // https://jira.vk.team/browse/CLOUDWEB-18747
            initAutoUploadAsidePromo, // https://jira.vk.team/browse/CLOUDWEB-18690
            initSuggestionsLawPromo, // https://jira.vk.team/browse/CLOUDWEB-15845
            initFamilyAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-15528
            initSafetyAsidePromoModal,
            initExpiredDeviceSubscriptionPromo,
            initCloudNewFeaturesMobilePromo,
            initMobilePublicBannerPromo,
            initMobileSearchHistoryBanner,
            initMobileSearchBanner,
            initDownloadAppPromo
        );
    } else {
        // Desktop
        // Добавлены выше addOneOf(), т.к. немного отличаются в поведении и должны показываться чаще.
        yield initCSATSurvey(); // https://jira.vk.team/browse/CLOUDWEB-14332
        yield initSecondClickPromo();

        yield addOneOf(
            /**
             * !!!!!
             * Новые промки следует заводить через 3promo
             *
             * По новой политике партии очередь на саге устарела.
             *
             * Стоит смотреть что происходит в чатике Cloud/Frontend/Tech и
             * как решают проблему с синхронизацией очередей
             * !!!!!
             */
            initFlexibleQuotaPromo, // https://jira.vk.team/browse/CLOUDWEB-18683
            initWelcomeNewFamilyTariffDialog, // https://jira.vk.team/browse/CLOUD-4591
            initVigboSuccessBuyModal, // https://jira.vk.team/browse/CLOUDWEB-19429
            initVkMusicBundleModal, // https://jira.vk.team/browse/CLOUDWEB-19117
            initAutoUploadAsidePromo, // https://jira.vk.team/browse/CLOUDWEB-18708
            initFullscreen, // https://jira.vk.team/browse/CLOUDWEB-19094
            initPreOverquotaPromo, // https://jira.vk.team/browse/CLOUDWEB-19123 Показ модалки для бывших оверквотников
            // tempexp_18907-next-line
            initDiscountInOutflowLastDayPromo, // https://jira.vk.team/browse/CLOUDWEB-18907
            initAutoUploadOnboardingPromo, // https://jira.vk.team/browse/CLOUDWEB-18926
            initBeautifulAddressPromo, // https://jira.vk.team/browse/CLOUDWEB-19373
            initLinkVigboAccount, // https://jira.vk.team/browse/CLOUDWEB-19213

            initVigboTariffEnd, // https://jira.vk.team/browse/CLOUDWEB-19461
            initVigboRenewRemind, // https://jira.vk.team/browse/CLOUDWEB-19460

            showFamilyAsideMenuTooltip,
            showFamilyAlbumsTooltip,
            showFamilyDocumentsTooltip,

            initReferralProgramAsidePromo, // https://jira.vk.team/browse/CLOUDWEB-18086
            initReferralProgramSpacePromo, // https://jira.vk.team/browse/CLOUDWEB-18086
            initPaidUserReturnModal, // https://jira.vk.team/browse/CLOUDWEB-18851
            // tempexp_18926-next-line
            initExtendQuotaOfferModal, // https://jira.vk.team/browse/CLOUDWEB-18747
            initAiSetFakedoorPromo, // https://jira.vk.team/browse/CLOUDWEB-18828
            initCrossPromoVkMusicModal, // https://jira.vk.team/browse/CLOUDWEB-18855
            initCoverageCampaignAsidePromo, // https://jira.vk.team/browse/CLOUDWEB-18696
            initTariffSelectionPoll, // https://jira.vk.team/browse/CLOUDWEB-18455
            initCyberMonday2025Modal, // https://jira.vk.team/browse/CLOUDWEB-18474
            initDemoPromoTooltip, // https://jira.vk.team/browse/CLOUDWEB-18233
            initGamerBundleModal, // https://jira.vk.team/browse/CLOUDWEB-18335
            // tempexp_18089-next-line
            initParnersDiscountAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-18089
            initBlackFriday2024Modal, // https://jira.vk.team/browse/CLOUDWEB-18060
            initUsedSpacePostpaidModal, // https://jira.vk.team/browse/CLOUDWEB-18085
            initAutumnPromoModal, // https://jira.vk.team/browse/CLOUDWEB-17821
            initRestoreTrashbinFilesModal, // https://jira.vk.team/browse/CLOUDWEB-17905
            initThemedPublicPromo, // https://jira.vk.team/browse/CLOUDWEB-17826
            // tempexp_17532-next-line
            initLitresModal, // https://jira.vk.team/browse/CLOUDWEB-17532
            initReferralProgramPromoModal, // https://jira.vk.team/browse/CLOUDWEB-17439
            // tempexp_17086-next-line
            initDefaultTariffForkModal, // https://jira.vk.team/browse/CLOUDWEB-17086
            initSuggestionsLawPromo, // https://jira.vk.team/browse/CLOUDWEB-15845
            initCoEditPopupPromo,
            initEditorLLMButton,
            initBindingSpherumPromoModal, // https://jira.vk.team/browse/CLOUDWEB-14883
            initFamilyAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-15528
            initSafetyAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-14549
            initSidebarTrialPromoModal, // https://jira.vk.team/browse/CLOUDWEB-17157
            initQuotaPromo, // https://jira.vk.team/browse/CLOUDWEB-11514
            initMailSpaceTrialPromoModal, // https://jira.vk.team/browse/CLOUDWEB-17130
            initChangePublicDesignPromoToolbarInit, // https://jira.vk.team/browse/CLOUDWEB-17340
            initBannerPromo, // https://jira.vk.team/browse/CLOUDWEB-9392
            initWhatsNewPromo, // https://jira.vk.team/browse/CLOUDWEB-12239
            initThemeAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-17351
            initPdfWorkWithPagesPromo, // https://jira.vk.team/browse/CLOUDWEB-18023
            initInitMobAppPromo, // https://jira.vk.team/browse/CLOUDWEB-16735
            initRebrandingPromo, // https://jira.vk.team/browse/CLOUDWEB-17200

            // CLOUDWEB-17198: Раздел с фичами облака убрали, но планируют вернуть
            // initCloudNewFeaturesDesktopPromo,

            // tempexp_SRCH-70119-next-line
            initSearchTooltipPromo,
            initDiskOAsidePromoModal, // https://jira.vk.team/browse/CLOUDWEB-14630
            initAutosaveAsidePromoModal,
            initStoriesInGalleyPromo, // https://jira.vk.team/browse/CLOUDWEB-17271

            // Мой офис
            initPublicUploadPromo,
            initFaceFilterPromo,
            firebasePromo
        );

        yield initEpubPromo(); // https://jira.vk.team/browse/CLOUDWEB-15467
    }
}

export function* threePromoProcessing(wait: number) {
    // ждем, чтобы не вызывать update слишком часто
    yield delay(wait);
    const manager: Awaited<ReturnType<typeof getPromoManager>> = yield call(getPromoManager);
    // возобнавляем обработку
    manager.resume();
    // чекаем есть ли чего показать
    const hasActive: Awaited<ReturnType<typeof updatePromoManager>> = yield call(updatePromoManager);
    // если есть что показать - показываем приортетно
    if (hasActive) {
        yield put(
            addPromoToShowQueue({
                forceTopmost: true,
                type: EPromoType.threePromo,
                promoId: EPromoType.threePromo,
                onShow: noop,
                onClose: noop,
            })
        );
        yield put(promoShown(EPromoType.threePromo));
    } else {
        // если ниче не нашли - паузим обратно
        manager.pause();

        if (prune3promoFakeItemFromSagaQueue) {
            // Если не убирать, то 3промо залочит обе очереди!
            // Если убрать - в радары на каждый update() саги будет улетать радар "redraw"
            // из 3promo, если в момент что-то показывается
            yield put(removePromo(EPromoType.threePromo));
        }
    }

    return hasActive;
}

function* queueProcessing() {
    while (true) {
        const queue = yield* select(PromoSelectors.getPromoQueue);
        const current = yield* select(PromoSelectors.getCurrentPromo);
        let promosIndex = 0;
        let isPromoShown = false;

        const is3PromoShown = (yield* select(PromoSelectors.get3PromoShown)) && disableReduxPromoAfter3Promo;

        promoSagaLogger.info('update', {
            current,
            queue,
            is3PromoShown,
        });

        if (!current || (prune3promoFakeItemFromSagaQueue && current?.type === EPromoType.threePromo)) {
            const threePromoActive = yield threePromoProcessing(PROMO_CONTROLLER_CHECK_INTERVAL);
            if (threePromoActive) {
                // ливаем из дальнейшей обработки, если 3promo что-то показывает
                continue;
            }
        }

        if (!current && queue.length > 0 && !is3PromoShown) {
            while (promosIndex < queue.length && !isPromoShown) {
                // заполняем currentPromo
                yield put(showPromo(queue[promosIndex].type));

                // ждём отклика от логики (c таймаутом)
                const { done, skip, timeout } = yield race({
                    done: take(promoShown),
                    skip: take(skipPromo),
                    ...(queue.length === 1 && !NO_LOCK_LAST_SAGA_PROMO ? {} : { timeout: delay(PROMO_WAIT_PERIOD) }),
                });

                // не было отклика или промо скипнули
                if (timeout || skip) {
                    promosIndex++;
                }

                // промо показали, выходим из цикла
                if (done) {
                    isPromoShown = true;
                }
            }

            // вышли из цикла выше с показом промо
            if (isPromoShown) {
                yield race({
                    done: take(removePromo),
                    skip: take(skipPromo),
                });
            } else {
                yield put(clearCurrentPromo());
            }

            yield delay(PERIOD_BETWEEN_PROMO);
        } else if (current) {
            // висит промо по системе pullPromo
            yield delay(PROMO_CONTROLLER_CHECK_INTERVAL);
        } else {
            // redux-очередь опустела, но в 3promo возможно что-то есть
            yield delay(NEWPROMO_MANAGER_CHECK_INTERVAL);
        }
    }
}

function* handleInitPromos({ payload }: ReturnType<typeof initPromoController>) {
    promoSagaLogger.info('init', {
        FILL_SAGA_PROMO_QUEUE_TIMEOUT,
        NEWPROMO_MANAGER_CHECK_INTERVAL,
        NO_LOCK_LAST_SAGA_PROMO,
        prune3promoFakeItemFromSagaQueue,
        disableReduxPromoAfter3Promo,
    });
    if (payload?.disable) {
        // не является попапом и промкой с точки зрения промо контроллета
        closeCSATSurvey();
    }

    if (IS_REACT_PROMO_NEW_YEAR || IS_FRAME_PAGES || payload?.disable) {
        return;
    }

    try {
        yield awaitUserLoad();
        yield put(clearQueue());

        if (FILL_SAGA_PROMO_QUEUE_TIMEOUT) {
            yield fillQueueWithTimeout(FILL_SAGA_PROMO_QUEUE_TIMEOUT);
        } else {
            yield fillQueue();
        }

        // tempexp_13536-next-line
        yield initDisableAdsBanner();
        yield initTopBanner();

        yield queueProcessing();
        // недосягаемый код, так как в queueProcessing бесконечный цикл!
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

// eslint-disable-next-line complexity
function* handleShowPromo({ payload }: ReturnType<typeof showPromo>) {
    const canShow = yield* select(isPromoCanBeShown);
    const isPhone = yield* select(EnvironmentSelectors.isPhone);

    if (!canShow) {
        return;
    }

    // Если промка не попала в эти ифы по EPromoType, то значит она показывается где-то в компоненте с помощью PromoSelectors.getPromo()
    if (payload === EPromoType.autologin) {
        yield showAutologinPromos();
    } else if (payload === EPromoType.banner) {
        yield showBannerPromo();
    } else if (payload === EPromoType.whatsNew) {
        yield showWhatsNewPromo();
    } else if (payload === EPromoType.downloadApp) {
        yield showDownloadAppPromo();
    } else if (payload === EPromoType.expiredDeviceSubscription) {
        yield showExpiredDeviceSubscriptionPromo();
    } else if (payload === EPromoType.mobileSearchHistoryBanner) {
        yield showMobileSearchHistoryBanner();
    } else if (payload === EPromoType.mobileSearchBanner) {
        yield showMobileSearchBanner();
    } else if (payload === EPromoType.mobilePublicBanner) {
        yield showMobilePublicBannerPromo();
    } else if (payload === EPromoType.quota) {
        yield showQuotaPopup();
    } else if (payload === EPromoType.csatSurvey) {
        yield showCSATSurvey();
    } else if (payload === EPromoType.diskOAsidePromo) {
        yield showDiskOAsidePromoModal();
    } else if (payload === EPromoType.safetyAsidePromo) {
        yield showSafetyAsidePromoModal();
    } else if (payload === EPromoType.bindingSpherum) {
        yield showBindingSpherumPromoModal();
    } else if (payload === EPromoType.autosave) {
        yield showAutosaveAsidePromoModal();
    } else if (payload === EPromoType.suggestionsLaw) {
        yield showSuggestionsLawPromo();
    } else if (payload === EPromoType.familyAsidePromo) {
        yield showFamilyAsidePromoModal();
    } else if (payload === EPromoType.sidebarTrialPromo) {
        yield showSidebarTrialPromoModal();
    } else if (payload === EPromoType.mailspaceTrialPromo) {
        yield showMailSpaceTrialPromoModal();
        // tempexp_17086-next-line
    } else if (payload === EPromoType.defaultTariffFork) {
        yield showDefaultTariffForkModal();
    } else if (payload === EPromoType.rebranding) {
        yield showRebrandingPromoModal();
        // tempexp_17480-next-line
    } else if (payload === EPromoType.themeAsidePromo) {
        yield showThemeAsidePromoModal();
        // tempexp_17532-next-line
    } else if (payload === EPromoType.litresModal) {
        yield showLitresModal();
        // tempexp_17439-next-line
    } else if (payload === EPromoType.referralProgramPromo) {
        yield showReferralProgramPromoModal();
        // tempexp_17826-next-line
    } else if (payload === EPromoType.themedPublicPromo) {
        yield showThemedPublicPromo();
        // tempexp_17821-next-line
    } else if (payload === EPromoType.autumnPromo) {
        yield showAutumnPromoModal();
        // tempexp_17905-next-line
    } else if (payload === EPromoType.restoreTrashbinFiles) {
        yield showRestoreTrashbinFilesModal();
        // tempexp_18060-next-line
    } else if (payload === EPromoType.blackFriday2024) {
        yield showBlackFriday2024Modal();
        // tempexp_18085-next-line
    } else if (payload === EPromoType.usedSpacePostpaid) {
        yield showUsedSpacePostpaidModal();
    } else if (payload === EPromoType.gamerBundle) {
        yield showGamerBundleModal();
        // tempexp_18474-next-line
    } else if (payload === EPromoType.cyberMonday2025) {
        yield showCyberMonday2025Modal();
        // tempexp-18683-next-line
    } else if (payload === EPromoType.flexibleQuotaPromo) {
        yield showFlexibleQuotaPromo();
        // tempexp_18089-next-line
    } else if (payload === EPromoType.partnersDiscount) {
        yield showParnersDiscountAsidePromoModal();
        // tempexp_18455-next-line
    } else if (payload === EPromoType.tariffSelectionPoll) {
        yield showTariffSelectionPoll();
        // tempexp_18696-next-line
    } else if (payload === EPromoType.coverageCampaign) {
        yield showCoverageCampaignAsidePromo();
        // tempexp_18708-next-line
    } else if (payload === EPromoType.autoUpload && !isPhone) {
        yield showAutoUploadAsidePromo();
        // tempexp_18851-next-line
    } else if (payload === EPromoType.paidUserReturn) {
        yield showPaidUserReturnModal();
    } else if (payload === EPromoType.extendQuotaOfferModal) {
        yield showExtendQuotaOfferModal();
        // tempexp_18855-next-line
    } else if (payload === EPromoType.crossPromoVkMusicFakedoor) {
        yield showCrossPromoVkMusicModal();
        // tempexp_18828-next-line
    } else if (payload === EPromoType.aiSetAb) {
        yield showAiSetFakedoorPromo();
        // tempexp_18907-next-line
    } else if (payload === EPromoType.discountInOutflowLastDayPromo) {
        yield showDiscountInOutflowLastDayPromo();
        // tempexp_18086-next-line
    } else if (payload === EPromoType.referralProgramAsidePromo) {
        yield showReferralProgramAsideModal();
        // tempexp_18926-next-line
    } else if (payload === EPromoType.autoUploadOnboarding) {
        yield showAutoUploadOnboardingPromo();
    } else if (payload === EPromoType.beautifulAddressModal) {
        yield showBeautifulAddressPromo();
    } else if (payload === EPromoType.fullScreenCloud) {
        yield showFullscreenDialog();
    } else if (payload === EPromoType.vkMusicBundle) {
        yield showVkMusicBundleModal();
    } else if (payload === EPromoType.preOverquotaPromo) {
        yield showPreOverquotaPromo();
    } else if (payload === EPromoType.showFamilyBuyModal) {
        yield showFamilyBuyModal();
    } else if (payload === EPromoType.showVigboBuyModal) {
        yield showVigboSuccessBuyModal();
    } else if (payload === EPromoType.vigboRenewRemind) {
        yield showVigboRenewRemind();
    } else if (payload === EPromoType.vigboTariffEnd) {
        yield showVigboTariffEnd();
    }
}

const FOOTER_HEIGHT = 43;
const ADD_BANNER_HEIGHT = 64;

function* handleShowMobileAppPromo() {
    const bottom = IS_PUBLIC_FOLDER ? 0 : FOOTER_HEIGHT + (HIDE_ADS ? 0 : ADD_BANNER_HEIGHT);

    yield put(setBottomMarginAction(bottom));
}

export function* promoRoot() {
    yield takeEvery(pushCSATTriggerEvent.toString(), handlePushCSATTriggerEvent);
    yield takeEvery(initPromoController.toString(), handleInitPromos);
    yield takeLatest(showPromo.toString(), handleShowPromo);
    yield takeLatest(showMobileAppPromo.toString(), handleShowMobileAppPromo);
}
